import { useSelector } from 'react-redux';
import { formatSpeed, formatDistance } from '@/lib/utils';

// Raw Traccar speeds are KNOTS; staff positions and status thresholds are km/h.
const KNOTS_TO = { kmh: 1.852, mph: 1.15078, kn: 1 };
const KMH_PER_UNIT = { kmh: 1, mph: 1.609344, kn: 1.852 };
const KM_PER_DIST_UNIT = { km: 1, mi: 1.609344, nmi: 1.852 };
const SPEED_LABELS = { kmh: 'km/h', mph: 'mph', kn: 'kn' };
const DISTANCE_LABELS = { km: 'km', mi: 'mi', nmi: 'nmi' };

/** Pure converters for memo-heavy call sites — depend on the `speedUnit`
 *  string, not on hook-returned function identities. */
export function knotsToUnit(knots, unit) {
  return (Number(knots) || 0) * (KNOTS_TO[unit] ?? KNOTS_TO.kmh);
}
export function kmhToUnit(kmh, unit) {
  return kmh == null ? null : kmh / (KMH_PER_UNIT[unit] ?? 1);
}
export function kmToUnit(km, unit) {
  return km == null ? null : km / (KM_PER_DIST_UNIT[unit] ?? 1);
}

/**
 * Per-user display units from Settings → Account → Units & Formats
 * (user.attributes.speedUnit / distanceUnit). Mirrors useCoordinateFormat.
 * Display-only: status/rule/alert thresholds stay km/h internally — convert
 * at the render site, never before comparing against a threshold.
 */
export default function useUnits() {
  const user = useSelector((state) => state.session.user);
  const rawSpeed = user?.attributes?.speedUnit;
  const rawDistance = user?.attributes?.distanceUnit;
  const speedUnit = SPEED_LABELS[rawSpeed] ? rawSpeed : 'kmh';
  const distanceUnit = DISTANCE_LABELS[rawDistance] ? rawDistance : 'km';
  return {
    speedUnit,
    distanceUnit,
    speedLabel: SPEED_LABELS[speedUnit],
    distanceLabel: DISTANCE_LABELS[distanceUnit],
    /** knots → displayed unit, rounded (same return contract as lib formatSpeed) */
    formatSpeed: (knots) => formatSpeed(knots, speedUnit),
    /** a value already in km/h (staff positions) → displayed unit, unrounded */
    speedFromKmh: (kmh) => kmhToUnit(kmh, speedUnit),
    /** meters → displayed unit string */
    formatDistance: (meters) => formatDistance(meters, distanceUnit),
  };
}
